import { Button, Grid, Link as MuiLink, Stack, Tooltip, Typography } from '@mui/material'
import { MouseEvent, useContext, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'

import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined'
import EmailOutlined from '@mui/icons-material/EmailOutlined'
import { enqueueSnackbar } from 'notistack'
import { Affected, NetworkTreeDto, NetworkTreeNode, NetworkTreeOption } from '../../interfaces/IAffectedCustomers'
import NetworkTreeSearchField from './NetworkTreeSearchField'
import ResponsiveDivider from '../../components/Divider/ResponsiveDivider'
import { eventIdType } from '../../utils/ValueFormatters'
import { EventId } from '../../interfaces/IEvent'
import { CustomContainer } from '../../components/Wrapper/CustomContainer'
import RootBreadcrumb from '../../components/navigation/RootBreadcrumb'
import { EbStack, EbSwitch, LoadingIndicator } from '@eidsivabredband/react-component-ui-package'
import AddAffectedEntitiesField from './AddAffectedEntitiesField'
import NetworkTreeBox from './NetworkTreeBox'
import InfoIcon from '@mui/icons-material/Info'
import { EventTypeShort } from '../../interfaces/IEventEnums'

function AffectedEntities() {
  const [networkTreeOptions, setNetworkTreeOptions] = useState<NetworkTreeOption[]>([])
  const [selectedNetworkTreeOptions, setSelectedNetworkTreeOptions] = useState<NetworkTreeOption[]>()

  const [initialAffectedPhoneNumbers, setInitialAffectedPhoneNumbers] = useState<string[]>([])
  const [initialAffectedEmailAddresses, setInitialAffectedEmailAddresses] = useState<string[]>([])

  const [networkTree, setNetworkTree] = useState<NetworkTreeDto[]>()
  const [affectedPhoneNumbers, setAffectedPhoneNumbers] = useState<string[]>([])
  const [affectedEmailAddresses, setAffectedEmailAddresses] = useState<string[]>([])

  const [showNetworkTreeBox, setShowNetworkTreeBox] = useState(false)
  const [loadingInitial, setLoadingInitial] = useState(false)
  const [loadingNetworkTree, setLoadingNetworkTree] = useState(false)
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false)
  const [loadingEmailAddresses, setLoadingEmailAddresses] = useState(false)
  const [buttonLoading, setButtonLoading] = useState(false)
  const [treeExpanded, setTreeExpanded] = useState(true)
  const [isAllCompanyCustomersAffected, setIsAllCompanyCustomersAffected] = useState(false)
  const isAllSelected = networkTree ? networkTree.every((node) => node.isChecked) : false

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const openMenu = Boolean(anchorEl)
  const handleMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const navigate = useNavigate()
  const eventId = useParams()
  const eventType = eventIdType(eventId.id as EventId)
  const { context } = useContext(EbAuthenticatedAppContext)

  const findNewNetworkTreeOptionsSelected = (selected: NetworkTreeOption[], searchResult: NetworkTreeDto[]) => {
    const selectedIds = selected.map((x) => x.id)
    const searchResultIds = searchResult.map((x) => x.id)

    const difference = selectedIds.filter((x) => !searchResultIds.includes(x))

    return difference
  }

  const findRemovedNetworkTreeOptionsSelected = (selected: NetworkTreeOption[], searchResult: NetworkTreeDto[]) => {
    const selectedIds = selected.map((x) => x.id)
    const searchResultIds = searchResult.map((x) => x.id)

    const difference = searchResultIds.filter((x) => !selectedIds.includes(x))

    return difference
  }

  const handleGoBackToEvent = () => {
    navigate(`/events/${eventId.id}`)
  }

  const toggleAllNodes = (isAllSelected: boolean) => {
    if (!networkTree) return
    const updatedTreeData = [...networkTree]
    const updateNode = (nodes: NetworkTreeDto[]) => {
      for (const node of nodes) {
        node.isChecked = !isAllSelected
        if (node.childNodes) {
          updateNode(node.childNodes)
        }
      }
    }
    updateNode(updatedTreeData)
    setNetworkTree(updatedTreeData)
  }

  const toggleNodesById = (ids: string[], isChecked: boolean) => {
    if (!networkTree || ids.length === 0) return
    const idSet = new Set(ids)
    const updatedTreeData = [...networkTree]
    const updateNode = (nodes: NetworkTreeDto[]) => {
      for (const node of nodes) {
        if (idSet.has(node.id)) {
          node.isChecked = isChecked
        }
        if (node.childNodes) {
          updateNode(node.childNodes)
        }
      }
    }
    updateNode(updatedTreeData)
    setNetworkTree(updatedTreeData)
  }

  const createNetworkTreeOption = (networkTree: NetworkTreeDto | NetworkTreeNode): NetworkTreeOption => {
    let label: string

    if (networkTree.type === 'ServicePoint' && networkTree.circuitNumbers) {
      label =
        networkTree.circuitNumbers?.map((circuitNumber) => circuitNumber).join(', ') +
        ` (${networkTree.model ? networkTree.model : networkTree.type})`
    } else if (networkTree.type === 'Equipment' && networkTree.managementIp) {
      label = networkTree.name + ` / ${networkTree.managementIp} (${networkTree.model ? networkTree.model : networkTree.type})`
    } else {
      label = networkTree.name + ` (${networkTree.model ? networkTree.model : networkTree.type})`
    }

    return {
      id: networkTree.id,
      label: label,
    }
  }

  const handleNetworkSelectedOptions = (selected: NetworkTreeOption[]) => {
    if (!selected || selected.length === 0) {
      setNetworkTree(undefined)

      setShowNetworkTreeBox(false)

      return
    }

    const newOptions = findNewNetworkTreeOptionsSelected(selected, networkTree ?? [])

    if (newOptions.length === 0) {
      const removedOptions = findRemovedNetworkTreeOptionsSelected(selected, networkTree ?? [])

      if (removedOptions.length === 0) {
        return
      }

      setNetworkTree((networkTreeValue) => {
        return networkTreeValue?.filter((node) => !removedOptions.includes(node.id))
      })

      return
    }

    const fetchData = async () => {
      setLoadingNetworkTree(true)
      setShowNetworkTreeBox(true)

      const response = await context.restApiClient
        .buildRequest('/api/network-tree')
        .serializeToJsonContent(newOptions)
        .deserializeResponseToJson<NetworkTreeDto[]>()
        .putAsync()

      if (response.json) {
        const networkTreeResponse = response.json
        setNetworkTree([...(networkTree ?? []), ...networkTreeResponse])
      } else {
        enqueueSnackbar(<Typography variant="body2">Noe gikk galt. Prøv igjen eller kontakt systemansvarlig.</Typography>, { variant: 'error' })
      }

      setLoadingNetworkTree(false)
    }

    fetchData()
  }

  const handleGetAffectedOnEvent = async (eventId: string) => {
    setLoadingInitial(true)

    var response = await context.restApiClient.buildRequest(`/api/events/${eventId}/get-affected`).deserializeResponseToJson<Affected>().getAsync()

    if (response.json) {
      const affected = response.json

      setNetworkTreeOptions(
        affected.networkTree?.map((networkTree: NetworkTreeDto) => {
          return createNetworkTreeOption(networkTree)
        }) ?? [],
      )

      setSelectedNetworkTreeOptions(
        affected.networkTree?.map((networkTree: NetworkTreeDto) => {
          return createNetworkTreeOption(networkTree)
        }) ?? [],
      )

      setNetworkTree(affected.networkTree)
      setInitialAffectedPhoneNumbers(affected.affectedPhoneNumbers)
      setInitialAffectedEmailAddresses(affected.affectedEmailAddresses)
      setIsAllCompanyCustomersAffected(affected.isAllCompanyCustomersAffected ?? false)

      setShowNetworkTreeBox(true)

      if (affected.networkTree && affected.networkTree.length === 0) setShowNetworkTreeBox(false)
    }

    if (response.error) {
      enqueueSnackbar(<Typography variant="body2">Noe gikk galt. Prøv igjen eller kontakt systemansvarlig.</Typography>, { variant: 'error' })
    }

    setLoadingInitial(false)
  }

  useEffect(() => {
    if (eventId.id && networkTreeOptions.length === 0 && affectedEmailAddresses.length === 0 && affectedPhoneNumbers.length === 0) {
      handleGetAffectedOnEvent(eventId.id)
    }
  }, [eventId])

  const handleSubmit = async (e: any) => {
    e.preventDefault()

    setButtonLoading(true)

    const affected: Affected = {
      networkTree: networkTree,
      isAllCompanyCustomersAffected: isAllCompanyCustomersAffected,
      affectedPhoneNumbers: affectedPhoneNumbers,
      affectedEmailAddresses: affectedEmailAddresses,
    }

    const response = await context.restApiClient
      .buildRequest(`/api/events/${eventId.id}/affected/update`)
      .serializeToJsonContent(affected)
      .postAsync()

    if (response.error) {
      if (response.error.errorCode === 409) {
        const isAggregateLock = response.error.errorText?.includes('__ALL_COMPANY_CUSTOMERS__')
        enqueueSnackbar(
          <Typography variant="body2">
            {isAggregateLock
              ? 'Kan ikke skru av «Alle bedriftskunder berørt» så lenge et aktivt varsel sendes til alle bedriftskunder. Slett varselet først.'
              : 'Kan ikke fjerne berørte kunder/telefonnumre/e-poster som er brukt i et aktivt varsel. Slett varselet eller fjern mottakeren fra varselet først.'}
          </Typography>,
          { variant: 'warning' },
        )
      } else {
        enqueueSnackbar(<Typography variant="body2">Kunne ikke oppdatere berørte kunder. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
          variant: 'error',
        })
      }
      setButtonLoading(false)
    } else {
      enqueueSnackbar(<Typography variant="body2">Berørte kunder oppdatert.</Typography>, { variant: 'success' })
      setButtonLoading(false)
      return handleGoBackToEvent()
    }
  }

  return (
    <CustomContainer maxWidth={'xl'}>
      <RootBreadcrumb>
        <MuiLink component={Link} underline="always" color="inherit" to={`/events/${eventId.id}`} sx={{ display: 'flex', alignItems: 'center' }}>
          {eventId.id}
        </MuiLink>
        <Typography>{'Berørte kunder'}</Typography>
      </RootBreadcrumb>
      <Typography
        variant="h5"
        sx={{
          marginTop: 4,
        }}
      >
        Berørte kunder
      </Typography>
      <ResponsiveDivider />
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {eventType !== EventTypeShort.Generell && (
            <Grid size={12}>
              <EbStack direction={'row'}>
                <EbSwitch
                  value={isAllCompanyCustomersAffected}
                  checked={isAllCompanyCustomersAffected}
                  onChange={(event) => {
                    setNetworkTree(undefined)
                    setSelectedNetworkTreeOptions(undefined)
                    setIsAllCompanyCustomersAffected((prevState) => !prevState)
                  }}
                  label={'Alle bedriftskunder berørt'}
                ></EbSwitch>
                <Tooltip title={'Ved varsling til alle bedriftskunder, varsles det via sms til kontaktpersonen som har rollen teknisk.'}>
                  <InfoIcon color={'primary'} />
                </Tooltip>
              </EbStack>
            </Grid>
          )}
          <Grid
            columnSpacing={4}
            rowSpacing={4}
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <AddAffectedEntitiesField
              initialEntities={initialAffectedPhoneNumbers}
              loadingInitial={loadingInitial}
              onChange={(event, value) => setAffectedPhoneNumbers(value)}
              onLoading={(value) => setLoadingPhoneNumbers(value)}
              title={isAllCompanyCustomersAffected ? 'Legg til ekstra telefonnummer' : 'Legg til telefonnummer'}
              placeholder="Splitt med , ; eller mellomrom"
              type="telefonnummer"
              urlPath="/phone-number"
              icon={<LocalPhoneOutlinedIcon />}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <AddAffectedEntitiesField
              initialEntities={initialAffectedEmailAddresses}
              loadingInitial={loadingInitial}
              onChange={(event, value) => setAffectedEmailAddresses(value)}
              onLoading={(value) => setLoadingEmailAddresses(value)}
              title={isAllCompanyCustomersAffected ? 'Legg til ekstra epostadresser' : 'Legg til epostadresser'}
              placeholder="Splitt med , ; eller mellomrom"
              type="epostadresser"
              urlPath="/email-address"
              icon={<EmailOutlined />}
            />
          </Grid>
          {eventType !== 'GE' && (
            <Grid size={12}>
              <>
                <Stack spacing={2}>
                  <NetworkTreeSearchField
                    options={networkTreeOptions}
                    disabled={isAllCompanyCustomersAffected}
                    selectedOptionsValue={selectedNetworkTreeOptions || []}
                    key={selectedNetworkTreeOptions?.length || 0}
                    loadingInitial={loadingInitial}
                    onSelectedOptions={handleNetworkSelectedOptions}
                  />
                  <NetworkTreeBox
                    showNetworkTreeBox={showNetworkTreeBox}
                    loadingNetworkTree={loadingNetworkTree}
                    networkTree={networkTree}
                    isAllSelected={isAllSelected}
                    toggleAllNodes={toggleAllNodes}
                    toggleNodesById={toggleNodesById}
                    treeExpanded={treeExpanded}
                    setTreeExpanded={setTreeExpanded}
                    openMenu={openMenu}
                    anchorEl={anchorEl}
                    handleMenuClick={handleMenuClick}
                    handleMenuClose={handleMenuClose}
                    enqueueSnackbar={enqueueSnackbar}
                  />
                </Stack>
              </>
            </Grid>
          )}
          <Grid size={12}>
            <Stack direction={'row'} spacing={'auto'}>
              <Button disabled={buttonLoading} variant="contained" color="error" size="large" onClick={handleGoBackToEvent}>
                Avbryt
              </Button>
              <Button
                sx={{
                  m: 0,
                }}
                disabled={loadingEmailAddresses || loadingPhoneNumbers || buttonLoading || loadingInitial}
                startIcon={buttonLoading ? <LoadingIndicator size={'xsmall'} sx={{ m: 0 }} /> : null}
                variant="contained"
                size="large"
                type="submit"
              >
                Lagre
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </CustomContainer>
  )
}

export default AffectedEntities
